import { useEffect, useState } from 'react';

import { cohortsApi } from '../api';
import type { CohortDetail } from '../types';

export interface UseCohortDetailResult {
  detail: CohortDetail | null;
  detailLoaded: boolean;
  detailErrorStatus: number | null;
  /** Caller holds any membership role: may read the member list and Cohort Info. */
  isMember: boolean;
  /** Owner/co-facilitator: progress, member actions and Discussions moderation. */
  canModerate: boolean;
}

const MEMBER_ROLES = new Set(['owner', 'co_facilitator', 'member']);
const MODERATOR_ROLES = new Set(['owner', 'co_facilitator']);

/**
 * Fetches a cohort's detail and derives what the caller may reach from its
 * `my_role`: any member reads the member list, only owner/co-facilitator moderate.
 */
export function useCohortDetail(slug: string | undefined): UseCohortDetailResult {
  const [detail, setDetail] = useState<CohortDetail | null>(null);
  const [detailLoaded, setDetailLoaded] = useState<boolean>(false);
  const [detailErrorStatus, setDetailErrorStatus] = useState<number | null>(null);

  const isMember = !!detail && MEMBER_ROLES.has(detail.my_role);
  const canModerate = !!detail && MODERATOR_ROLES.has(detail.my_role);

  useEffect(() => {
    if (!slug) {
      return;
    }

    cohortsApi.getCohortDetail(
      slug,
      (data) => {
        setDetail(data);
        setDetailLoaded(true);
      },
      (error) => {
        setDetailErrorStatus((error as { status?: number })?.status ?? null);
        setDetailLoaded(true);
      },
    );
  }, [slug]);

  return {
    detail,
    detailLoaded,
    detailErrorStatus,
    isMember,
    canModerate,
  };
}
